import * as React from 'react';
import { cn, getEvColorClass, type EVVariant } from '../utils';
import { TeaseSurface } from './TeaseSurface';
import type { BookLine } from '@sharpr/types';
import { presentationFor } from './books';
import { useBook } from './bookCatalog';
import {
  betLimitTier,
  showsLiquidityTier,
  DEFAULT_LIQUIDITY_THRESHOLDS,
  TIER_DOT_CLASS,
  type LiquidityThresholds,
  type LiquidityTier,
} from './liquidity';
import { formatEV, formatOdds } from './format';

// Used in BestLineSummary to color the leading-edge accent bar as a tier
// indicator. Leading-bar pattern (Linear / GitHub check rows) — uniform
// footprint across rows, visible regardless of thumbnail fill.
const TIER_BAR_CLASS: Record<LiquidityTier, string> = {
  dead: 'bg-red-500',
  thin: 'bg-orange-500',
  ok: 'bg-yellow-400',
  deep: 'bg-emerald-500',
};

export interface LineDisplayProps {
  line: BookLine & { ev?: number; pWin?: number };
  showThumbnail?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  evVariant?: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  /** When the line has no EV (stripped for non-subscribers), render a blurred ghost
   *  value in the EV slot instead of blank — a "sign up to see EV" teaser. Same slot
   *  footprint, so no layout shift. The actual sign-up CTA lives at the app layer
   *  (upsell banner + row-click → /signup). */
  teaseEv?: boolean;
  /** Markets with no projection model yet (currently MLB stolen bases): show a construction
   *  glyph in the EV slot for ALL viewers instead of the sign-up teaser — signing up
   *  wouldn't unlock these, so the blur teaser would mislead. Takes precedence over teaseEv. */
  teaseConstruction?: boolean;
}

export const LineDisplay: React.FC<LineDisplayProps> = ({
  line,
  showThumbnail = false,
  className,
  size = 'md',
  evVariant = 'default',
  liquidityThresholds = DEFAULT_LIQUIDITY_THRESHOLDS,
  teaseEv = false,
  teaseConstruction = false,
}) => {
  const { name: bookName, book } = useBook(line.book);
  const presentation = presentationFor(line.book, bookName);
  const hasEV = line.ev !== undefined;
  const isRemoved = line.isRemoved === true;
  const evColorClass = hasEV ? getEvColorClass(line.ev!, evVariant) : 'text-muted-foreground';

  // EV slot content — real value, construction glyph (no-model markets), blurred
  // teaser (non-subs), or blank. Shared by both layout branches so the slot reads
  // identically.
  const evContent = hasEV ? (
    <>{formatEV(line.ev!)}</>
  ) : isRemoved ? (
    <span>&nbsp;</span>
  ) : teaseConstruction ? (
    <TeaseSurface
      title="Projections coming soon"
      body="We don't yet have a model for this market. Odds are live; projections + EV land in a future release."
      side="top"
      wrapperClassName="flex w-full items-center justify-center"
    >
      <span className="flex items-center justify-center text-amber-500 dark:text-amber-400" aria-label="Projections coming soon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="6" width="20" height="8" rx="1" />
          <path d="M17 14v7" /><path d="M7 14v7" /><path d="M17 3v3" /><path d="M7 3v3" />
          <path d="M10 14 2.3 6.3" /><path d="m14 6 7.7 7.7" /><path d="m8 6 8 8" />
        </svg>
      </span>
    </TeaseSurface>
  ) : teaseEv ? (
    <TeaseSurface
      title="Unlock EV"
      body="See per-line edge over the market. Refreshed every minute, with model projections behind every number."
      ctaLabel="Start free trial"
      ctaHref="/signup"
      secondaryLabel="Already have an account? Log in"
      secondaryHref="/login"
      side="top"
      wrapperClassName="inline-block"
      surface="book_line_ev"
    >
      <span className="blur-[3px] opacity-50 select-none" aria-label="Start a free trial to unlock EV">+0.0%</span>
    </TeaseSurface>
  ) : (
    <span>&nbsp;</span>
  );

  const sizeConfig = {
    sm: {
      edge: 'font-mono text-xs font-bold',
      line: 'text-xs',
      odds: 'font-mono text-xs',
      thumbnail: 'h-4 w-4',
      badge: 'h-4 w-4 text-[8px]'
    },
    md: {
      edge: 'font-mono text-sm font-bold',
      line: 'text-base',
      odds: 'font-mono text-sm',
      thumbnail: 'h-6 w-6',
      badge: 'h-6 w-6 text-[10px]'
    }
  };

  const config = sizeConfig[size];

  const hasBetLimit = typeof line.betLimit === 'number' && showsLiquidityTier(book?.limitKind ?? null);
  const tier = hasBetLimit ? betLimitTier(line.betLimit!, liquidityThresholds) : null;

  // Flex layout when showing thumbnails (BestLineSummary) — mirrors
  // SportsbookLineCell spacing so the two columns read identically.
  if (showThumbnail) {
    return (
      <div className={cn(
        "relative flex items-center justify-start gap-1.5 px-1.5 py-0.5 h-full w-full box-border",
        isRemoved && "opacity-50",
        className
      )}>
        {/* EV */}
        <div className={cn(
          config.edge,
          "shrink-0 w-9 text-left",
          isRemoved ? "text-muted-foreground" : evColorClass
        )}>
          {evContent}
        </div>

        {/* Line + odds */}
        <div className="flex items-center gap-1 min-w-0 ml-auto">
          <span className={cn(
            config.line,
            isRemoved && "text-muted-foreground"
          )}>
            <span>{line.side === 'over' ? 'o' : 'u'}</span>
            <span className="font-mono">{line.line.toFixed(1)}</span>
          </span>
          <span className={cn(config.odds, "text-muted-foreground")}>
            ({formatOdds(line.odds)})
          </span>
        </div>

        {/* Book thumbnail */}
        <div className="shrink-0 flex justify-center">
          {presentation.thumbnail ? (
            <img
              src={presentation.thumbnail}
              alt={bookName}
              className={cn(config.thumbnail, "rounded object-cover", presentation.thumbnailScale)}
              loading="lazy"
            />
          ) : (
            <div className={cn(
              config.badge,
              "rounded bg-muted flex items-center justify-center"
            )}>
              <span className="font-bold text-muted-foreground">
                {presentation.abbreviation}
              </span>
            </div>
          )}
        </div>

        {/* Liquidity tier — quarter-circle anchored to the outer corner,
            matching SportsbookLineCell. Now that the grid-row chain gives each
            slot its full half-height, the dot sits cleanly at the row corner. */}
        {tier && (
          <span
            aria-hidden
            className={cn(
              'absolute w-[7px] h-[7px] pointer-events-none',
              TIER_BAR_CLASS[tier],
              line.side === 'over' ? 'top-0 right-0 rounded-bl-full' : 'bottom-0 right-0 rounded-tl-full'
            )}
          />
        )}
      </div>
    );
  }

  // Flex layout for sportsbook cell lines (no thumbnails).
  // h-full + w-full fills the parent slot (grid-row cell in SportsbookLineCell).
  return (
    <div className={cn(
      "relative flex items-center justify-start gap-3 px-1.5 py-0.5 h-full w-full box-border",
      isRemoved && "opacity-50",
      className
    )}>
      {/* EV */}
      <div className={cn(
        config.edge,
        "shrink-0 w-9 text-left",
        isRemoved ? "text-muted-foreground" : evColorClass
      )}>
        {evContent}
      </div>

      {/* Line details */}
      <div className="flex items-center gap-1 min-w-0 ml-auto">
        <span className={cn(
          config.line,
          isRemoved && "text-muted-foreground"
        )}>
          <span>{line.side === 'over' ? 'o' : 'u'}</span>
          <span className="font-mono">{line.line.toFixed(1)}</span>
        </span>
        <span className={cn(config.odds, "text-muted-foreground")}>
          ({formatOdds(line.odds)})
        </span>
      </div>

      {/* Liquidity tier — quarter-circle anchored to the outer corner of the slot */}
      {tier && (
        <span
          aria-hidden
          className={cn(
            'absolute w-[7px] h-[7px] pointer-events-none',
            TIER_DOT_CLASS[tier],
            line.side === 'over' ? 'top-0 right-0 rounded-bl-full' : 'bottom-0 right-0 rounded-tl-full'
          )}
        />
      )}
    </div>
  );
};
